import { type Dispatch, type SetStateAction, useState } from 'react'

import {
  FvButton,
  FvDate,
  FvLinkButton,
  QuoteCarrier,
} from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import { usePermissions } from '@/features/auth'
import { HideForVendor } from '@/features/auth/HasRole'
import { WorkflowEnabled } from '@/features/auth/HasWorkflow'
import { MiniQuoteList } from '@/features/mini-quote-list/MiniQuoteList'
import { quoteFuncs } from '@/features/quote/quoteFuncs'
import { useShowRouteGuide } from '@/features/route-guides/hooks'
import {
  useEditBookingPage,
  useQuoteRequestResponses,
  useQuotes,
} from '@/hooks/shipments'
import { routes } from '@/routes'

import InfoBox from '../../InfoBox'
import { Pinline } from '../../Pinline'
import { PricePlain } from '../../shared/Price'
import { DetailPanelLayout } from '../DetailPanelLayout'
import type { PanelName } from '../LoadDetailPanel'
import ShipmentActions from '../ShipmentActions'
import { PanelFooter } from './PanelFooter'
import { PanelHeaderBody, PanelHeaderNav } from './PanelHeader'
import RatesPanel from './RatesPanel'

type Props = {
  load: FullShipment
  setActivePanel: Dispatch<SetStateAction<PanelName>>
}

export const PendingLoadDetail = ({ load, setActivePanel }: Props) => {
  const responses = useQuoteRequestResponses({
    load,
  })
  const hasAnyResponses =
    responses.active.length ||
    responses.declined.length ||
    responses.pending.length
  const [isShowingRates, setShowingRates] = useState(false)
  const [isViewingActive, setViewingActive] = useState(true)
  const { canEdit, composeEditLink } = useEditBookingPage(load)
  const { status } = load
  const hasAwardedCarrier = status === 'awarded'
  const showRouteGuide = useShowRouteGuide(load)
  const showFullQuoteResponses =
    (!!hasAnyResponses || load.workflow === 'truckload') &&
    !load.isFreightCollect

  return (
    <DetailPanelLayout
      header={
        hasAwardedCarrier && isShowingRates ? (
          <PanelHeaderNav setShowingRates={setShowingRates} />
        ) : undefined
      }
      footer={
        <WorkflowEnabled workflow={load.workflow}>
          <PanelFooter
            isShowingRates={isShowingRates}
            load={load}
            setShowingRates={setShowingRates}
            setViewingActive={setViewingActive}
          />
        </WorkflowEnabled>
      }
    >
      {load.pickup?.status === 'error' && (
        <InfoBox icon="exclamation-triangle">
          Booking failed: {load.pickup.error ?? 'Unknown error'}.
          <WorkflowEnabled workflow={load.workflow}>
            {canEdit && (
              <>
                Please{' '}
                <FvLinkButton to={composeEditLink()} className="text-fv-blue">
                  update this load
                </FvLinkButton>{' '}
                and try booking again.
              </>
            )}
          </WorkflowEnabled>
        </InfoBox>
      )}

      {load.workflow !== 'truckload' && !hasAwardedCarrier && (
        <DirectRateInfoBox load={load} />
      )}

      <PanelHeaderBody load={load} hasAnyResponses={hasAnyResponses} />

      <HideForVendor>
        {showRouteGuide && !hasAwardedCarrier && (
          <FvButton
            className="self-start"
            icon="route"
            onClick={() => setActivePanel('routeGuide')}
          >
            Route guide
          </FvButton>
        )}
        {showFullQuoteResponses && (
          <>
            <Pinline className="mt-2 mb-4" />

            {(!hasAwardedCarrier || isShowingRates) && (
              <RatesPanel
                isViewingActive={isViewingActive}
                load={load}
                setShowRequestPanel={() => setActivePanel('request')}
                setViewingActive={setViewingActive}
              />
            )}

            {hasAwardedCarrier && !isShowingRates && (
              <WorkflowEnabled workflow={load.workflow}>
                <ShipmentActions
                  setActivePanel={setActivePanel}
                  shipment={load}
                  showRatesPanel={() => setShowingRates(true)}
                />
              </WorkflowEnabled>
            )}
          </>
        )}
        {!!hasAnyResponses && load.isFreightCollect && (
          <>
            <Pinline className="mt-2 mb-4" />
            <MiniQuoteList
              filter={'declined'}
              load={load}
              initialActiveId={responses.declined[0]?.id}
            />
          </>
        )}
      </HideForVendor>
    </DetailPanelLayout>
  )
}

const BookShipmentSentence = ({ load }: { load: FullShipment }) => {
  const { canBook } = usePermissions()
  if (!load.selectedQuote || !canBook) {
    return null
  }
  const bookRoute = routes.book({
    loadId: load.loadId,
    quoteId: load.selectedQuote?._id,
  })
  return (
    <>
      To book this shipment with the rate you selected{' '}
      <FvLinkButton theme="callout" to={bookRoute}>
        {' '}
        click here
      </FvLinkButton>
      .
    </>
  )
}

function DirectRateInfoBox({ load }: { load: FullShipment }) {
  const ratesRoute = routes.rates(load)
  const quotes = useQuotes(load.loadId)
  const { canViewRates } = usePermissions()
  const spotMessage =
    'If you sent out for spot quotes, they will be shown below.'
  const quote =
    load.selectedQuote ??
    load.cheapestQuote ??
    quoteFuncs.cheapestQuote(quoteFuncs.filterActiveDirect(quotes.data))
  const quoteDate = quoteFuncs.getRecentAPIQuoteDate(load, quotes.data)

  const quoteDetail = quote ? (
    <>
      <b>
        <PricePlain amount={quote?.amount} />
      </b>{' '}
      from{' '}
      <b>
        <QuoteCarrier quote={quote} />
      </b>
      .
    </>
  ) : null

  if (load.selectedQuote) {
    return (
      <InfoBox icon="hand-paper">
        You ran this quote on{' '}
        <b>
          <FvDate val={quoteDate} format="ddd MMM D" />
        </b>
        . You saved the rate for {quoteDetail} {spotMessage}
        <WorkflowEnabled workflow={load.workflow}>
          <BookShipmentSentence load={load} />
        </WorkflowEnabled>
      </InfoBox>
    )
  }

  return (
    <InfoBox icon="hand-paper">
      You ran this quote on{' '}
      <b>
        <FvDate val={quoteDate} format="ddd MMM D" />
      </b>
      .
      {canViewRates && (
        <>
          {quoteDetail && (
            <>The cheapest direct rate at that time was {quoteDetail}</>
          )}
          {spotMessage}
          <WorkflowEnabled workflow={load.workflow}>
            To compare all of your rates{' '}
            <FvLinkButton theme="underlined" to={ratesRoute}>
              click here
            </FvLinkButton>
            .
          </WorkflowEnabled>
        </>
      )}
    </InfoBox>
  )
}
